// src/components/avisEnseignants/DialogIncidentAvis.jsx
// ============================================================================
// « Créer un incident » à partir d'un avis d'enseignant
// ============================================================================
//
// Le seul chemin par lequel un fait signalé par un enseignant peut remonter aux
// parents : la vie scolaire crée ELLE-MÊME un événement, qu'elle rédige. Rien
// n'est pré-rempli depuis l'avis, sauf l'élève (décision de Daniel du
// 6 octobre 2026) — ni le titre, ni la description, ni la date : le texte de
// l'enseignant est un avis interne, écrit pour l'école ; ce qui part vers une
// famille doit être relu et réécrit par quelqu'un qui en répond.
//
// L'avis reste affiché à côté du formulaire, en lecture, pour que la personne
// qui rédige sache de quoi il s'agit.
//
// Le serveur crée l'événement avec les mêmes champs et valeurs par défaut que
// « Nouvel événement » (POST /api/vie-scolaire/evenements), puis passe l'avis
// au statut « incident ». Les valeurs par défaut de ce formulaire (Incident,
// Moyen) sont celles de « Nouvel événement », pour que la vie scolaire
// retrouve ses habitudes.
// ============================================================================

import React, { useEffect, useId, useRef, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import ChampSelect from '../ui/ChampSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { GRAVITE_OPTIONS, TYPE_OPTIONS } from '../../hooks/useVieScolaire';
import { useFeatures } from '../../contexts/FeaturesContext';
import { LibellesAvis, auteurAvis } from './presentationAvis';
import { avisEnseignants } from './apiAvisEnseignants';
import { dateLongue, libelleDe, nomEleve } from '../suiviEnseignants/formatSuivi';

// Les types que la route accepte pour un incident né d'un avis. Ni absence ni
// retard (ils viennent de l'appel), ni dégradation (elle exige une pièce
// jointe, que cette fenêtre ne prend pas).
const TYPES_PERMIS = ['incident', 'convocation', 'entretien', 'sanction', 'observation_positive', 'note_interne', 'autre'];
const TYPES = TYPE_OPTIONS
  .filter((o) => TYPES_PERMIS.includes(o.value))
  .map((o) => ({ valeur: o.value, libelle: o.label }));
const GRAVITES = GRAVITE_OPTIONS.map((o) => ({ valeur: o.value, libelle: o.label }));

// Les bornes du serveur (AvisEnseignantService : titre VARCHAR(180), description 5 000).
const TITRE_MAX = 180;
const DESCRIPTION_MAX = 5000;

const VIDE = { type: 'incident', gravite: 'moyen', date_evenement: '', titre: '', description: '' };

/**
 * Les refus qui ôtent son objet à la fenêtre : l'avis est déjà devenu un
 * incident ou son élève a disparu (409), l'avis n'existe plus (404), le droit
 * de traiter ou la licence de la vie scolaire ont été retirés (403). La page
 * ferme alors la fenêtre et relit sa liste.
 *
 * Tous les autres gardent la fenêtre ouverte, avec ce qui a été rédigé : une
 * saisie refusée (400) se corrige ; « avis en cours de modification ailleurs »
 * (409 AVIS_OCCUPE, un verrou) dit justement de réessayer ; une panne du
 * serveur (5xx) ou du réseau n'a rien écrit — la transaction est annulée — et
 * fermer ferait perdre une description entière.
 */
const refusDefinitif = (erreur) => [403, 404, 409].includes(erreur?.statut) && erreur?.code !== 'AVIS_OCCUPE';

const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15';
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const DialogIncidentAvis = ({ avis, onFermer, onCree, onRefus }) => {
  const [champs, setChamps] = useState(VIDE);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  // Verrou d'envoi immédiat : l'état `envoi` ne désactive le bouton qu'au
  // rendu suivant, une seconde soumission arrivée avant lui partirait aussi
  // (le serveur la refuserait en 409, mais la fenêtre se fermerait sur un
  // message d'erreur juste après le succès).
  const envoiEnCours = useRef(false);
  const { estActive } = useFeatures();
  const ids = {
    type: useId(),
    gravite: useId(),
    date: useId(),
    aideDate: useId(),
    titre: useId(),
    description: useId()
  };

  // Un autre avis : formulaire vierge. Rien ne doit survivre d'un élève à l'autre.
  useEffect(() => {
    setChamps(VIDE);
    setErreur(null);
  }, [avis?.id]);

  const changer = (cle, valeur) => setChamps((prec) => ({ ...prec, [cle]: valeur }));

  const titre = champs.titre.trim();
  const description = champs.description.trim();
  const complet = Boolean(titre && description && champs.type && champs.gravite);

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    if (!avis || !complet || envoiEnCours.current) return;
    envoiEnCours.current = true;
    setEnvoi(true);
    setErreur(null);
    try {
      const reponse = await avisEnseignants.creerIncident(avis.id, {
        type: champs.type,
        gravite: champs.gravite,
        titre,
        description,
        // Vide : le serveur date l'incident de son enregistrement.
        ...(champs.date_evenement ? { date_evenement: champs.date_evenement } : {})
      });
      onCree(reponse);
    } catch (e) {
      if (refusDefinitif(e)) onRefus(e);
      else setErreur(e.message);
    } finally {
      envoiEnCours.current = false;
      setEnvoi(false);
    }
  };

  const classe = libelleDe(avis?.eleve?.classe);
  const parentsOuverts = estActive('module.parents');

  return (
    <Dialog open={Boolean(avis)} onOpenChange={(ouvert) => { if (!ouvert && !envoi) onFermer(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        // Un clic à côté ne doit pas perdre une description en cours de rédaction.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <form onSubmit={soumettre} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Créer un incident</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {nomEleve(avis?.eleve)}{classe ? ` · ${classe}` : ''}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="grid flex-1 gap-4 overflow-y-auto px-5 py-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-4">
              {erreur && (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{erreur}</p>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label htmlFor={ids.type} className="mb-1 block text-sm text-gray-700">Type</label>
                  <ChampSelect id={ids.type} valeur={champs.type} onChange={(v) => changer('type', v)} options={TYPES} />
                </div>
                <div>
                  <label htmlFor={ids.gravite} className="mb-1 block text-sm text-gray-700">Gravité</label>
                  <ChampSelect id={ids.gravite} valeur={champs.gravite} onChange={(v) => changer('gravite', v)} options={GRAVITES} />
                </div>
                <div>
                  <label htmlFor={ids.date} className="mb-1 block text-sm text-gray-700">Date et heure</label>
                  <input
                    id={ids.date}
                    type="datetime-local"
                    className={CHAMP}
                    value={champs.date_evenement}
                    onChange={(e) => changer('date_evenement', e.target.value)}
                    aria-describedby={ids.aideDate}
                  />
                  <p id={ids.aideDate} className="mt-1 text-[11px] text-gray-500">Laissée vide : le moment de l'enregistrement.</p>
                </div>
              </div>

              <div>
                <label htmlFor={ids.titre} className="mb-1 block text-sm text-gray-700">Titre (obligatoire)</label>
                <input
                  id={ids.titre}
                  type="text"
                  className={CHAMP}
                  maxLength={TITRE_MAX}
                  required
                  aria-required="true"
                  value={champs.titre}
                  onChange={(e) => changer('titre', e.target.value)}
                />
              </div>

              <div>
                <label htmlFor={ids.description} className="mb-1 block text-sm text-gray-700">Description (obligatoire)</label>
                <textarea
                  id={ids.description}
                  rows={6}
                  maxLength={DESCRIPTION_MAX}
                  required
                  aria-required="true"
                  className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  value={champs.description}
                  onChange={(e) => changer('description', e.target.value)}
                />
              </div>
            </div>

            <aside className="space-y-3">
              <section className="rounded-lg border border-gray-200 bg-gray-50 p-3.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">L'avis de l'enseignant</p>
                <p className="mt-1 text-xs text-gray-500">
                  {[dateLongue(avis?.date_avis), auteurAvis(avis)].filter(Boolean).join(' · ')}
                </p>
                <div className="mt-2"><LibellesAvis libelles={avis?.libelles} /></div>
                {avis?.commentaire && (
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">{avis.commentaire}</p>
                )}
              </section>
              <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs leading-relaxed text-gray-700">
                Rien n'est recopié de l'avis : l'incident est rédigé par la vie scolaire, qui en répond.
                {parentsOuverts && (
                  <> Une fois traités, un incident ou une sanction apparaissent dans l'espace des familles sous leur
                    titre ; la description, elle, n'y est jamais montrée.</>
                )}
              </p>
            </aside>
          </div>

          <DialogFooter className="gap-2 border-t bg-gray-50 px-5 py-4 sm:space-x-0">
            <Button type="button" variant="outline" className={BOUTON_PIED} onClick={onFermer} disabled={envoi}>
              Annuler
            </Button>
            <Button
              type="submit"
              className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5a7a]`}
              disabled={!complet || envoi}
            >
              {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
              Créer l'incident
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DialogIncidentAvis;
